import {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  addWeeks, parseISO, differenceInWeeks,
} from 'date-fns';
import log from '@/common/utils/logger';
import { User } from '@/common/api/models/user';
import { Module } from '@/student/types';
import { StudyPlan, StudyPace, PACE_CONFIG } from '@/student/types/StudyPlan';

const STORAGE_KEY = 'student_study_plan_v1';

// Default enrollment if missing (fallback)
const DEFAULT_ENROLLMENT = '2025-10-01';

export type OnTrackStatus = 'ahead' | 'on-track' | 'behind';

interface UseStudyPlanReturn {
  studyPlan: StudyPlan | null;
  isLoading: boolean;

  // Computed values
  projectedGraduationDate: Date | null;
  totalWeeksRequired: number;
  onTrackStatus: OnTrackStatus;
  modulesCompleted: number;
  totalModules: number;
  progressPercentage: number;

  // Actions
  updatePace: (pace: StudyPace) => Promise<void>;
  updateModuleWeeks: (moduleId: string, weeks: number | null) => Promise<void>;
  resetPlan: () => Promise<void>;

  // Helpers
  getWeeksForModule: (moduleId: string) => number | null;
}

export const useStudyPlan = (user: User | null, modules: Module[]): UseStudyPlanReturn => {
  const [studyPlan, setStudyPlan] = useState<StudyPlan | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize plan
  useEffect(() => {
    const loadPlan = async () => {
      setIsLoading(true);
      try {
        const stored = await AsyncStorage.getItem(STORAGE_KEY);
        if (stored) {
          setStudyPlan(JSON.parse(stored));
        } else {
          // Create default plan
          const defaultPlan: StudyPlan = {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            enrollmentDate: (user?.cohort as any)?.startDate || DEFAULT_ENROLLMENT,
            pace: 'standard',
            hoursPerWeek: PACE_CONFIG.standard.hours,
            moduleAdjustments: {},
          };
          setStudyPlan(defaultPlan);
        }
      } catch (e) {
        log.error('Failed to load study plan', e as Error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPlan();
  }, [user]);

  // Persist on change
  const savePlan = async (newPlan: StudyPlan) => {
    setStudyPlan(newPlan);
    try {
      await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(newPlan));
    } catch (e) {
      log.error('Failed to save study plan', e as Error);
    }
  };

  const updatePace = useCallback(async (pace: StudyPace) => {
    if (!studyPlan) return;
    const config = PACE_CONFIG[pace];
    await savePlan({
      ...studyPlan,
      pace,
      hoursPerWeek: config.hours,
      // We keep module adjustments? Or clear them?
      // Decision: Keep manual overrides as they are specific user intent.
    });
  }, [studyPlan]);

  const updateModuleWeeks = useCallback(async (moduleId: string, weeks: number | null) => {
    if (!studyPlan) return;
    await savePlan({
      ...studyPlan,
      moduleAdjustments: {
        ...studyPlan.moduleAdjustments,
        [moduleId]: weeks,
      },
    });
  }, [studyPlan]);

  const resetPlan = useCallback(async () => {
    await AsyncStorage.removeItem(STORAGE_KEY);
    const defaultPlan: StudyPlan = {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      enrollmentDate: (user?.cohort as any)?.startDate || DEFAULT_ENROLLMENT,
      pace: 'standard',
      hoursPerWeek: PACE_CONFIG.standard.hours,
      moduleAdjustments: {},
    };
    setStudyPlan(defaultPlan);
  }, [user]);

  const getWeeksForModule = useCallback((moduleId: string) => {
    if (!studyPlan) return null;

    // Check override (including explicit null to disable)
    if (studyPlan.moduleAdjustments[moduleId] !== undefined) {
      return studyPlan.moduleAdjustments[moduleId];
    }

    // Default is off
    return null;
  }, [studyPlan]);

  const calculations = useMemo(() => {
    if (!studyPlan || !modules.length) {
      return {
        projectedGraduationDate: null,
        totalWeeksRequired: 0,
        onTrackStatus: 'on-track' as OnTrackStatus,
        modulesCompleted: 0,
        totalModules: 0,
        progressPercentage: 0,
      };
    }

    let totalWeeks = 0;
    modules.forEach((m) => {
      totalWeeks += getWeeksForModule(m.id) || PACE_CONFIG[studyPlan.pace].weeksPerModule;
    });

    const enrollment = parseISO(studyPlan.enrollmentDate);
    const graduation = addWeeks(enrollment, totalWeeks);

    const completedModules = modules.filter((m) => m.uiState === 'graded' || m.uiState === 'submitted').length;
    const totalCount = modules.length;
    const progress = (completedModules / totalCount) * 100;

    // Velocity / On-track logic
    const weeksSinceEnrollment = differenceInWeeks(new Date(), enrollment);
    const { weeksPerModule } = PACE_CONFIG[studyPlan.pace];
    const expectedModules = Math.floor(weeksSinceEnrollment / weeksPerModule);

    let status: OnTrackStatus = 'on-track';
    if (completedModules > expectedModules + 1) {
      status = 'ahead';
    } else if (completedModules < expectedModules) {
      status = 'behind';
    }

    return {
      projectedGraduationDate: graduation,
      totalWeeksRequired: totalWeeks,
      onTrackStatus: status,
      modulesCompleted: completedModules,
      totalModules: totalCount,
      progressPercentage: progress,
    };
  }, [studyPlan, modules, getWeeksForModule]);

  return {
    studyPlan,
    isLoading,
    ...calculations,
    updatePace,
    updateModuleWeeks,
    resetPlan,
    getWeeksForModule,
  };
};
